import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridPager, type KstDataGridHeaderButton, type KstDataGridRowAction } from '@/components/KstDataGrid';
import { navigateToAdminClientGroup } from '@/services/navigation';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { hasValue } from '@/utils/legacy-utils';

const PAGE_SIZE = 2000;

export function ClientGroupManagementScreen() {
  const { theme } = useTheme();

  const [entries, setEntries] = useState<any[] | null>(null);
  const [showingDisabledClients, setShowingDisabledClients] = useState(false);
  const [isClientGroupsLoading, setIsClientGroupsLoading] = useState<boolean>(false);
  // Default the list to alphabetical by client name (instead of creation/input order).
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<string>('asc');

  // We need a ref-like approach for the toggle button label, so we build it dynamically
  const headerButtons: KstDataGridHeaderButton[] = useMemo(() => [
    { name: 'New', function: 'add', src: '/Content/TourImages/add-r.svg' },
    { name: showingDisabledClients ? 'Hide Disabled' : 'Show All', function: 'toggledisabled', src: '/Content/TourImages/list-tree.svg' },
  ], [showingDisabledClients]);

  const viewClientGroup = useCallback((clientGroupId: number) => {
    navigateToAdminClientGroup(clientGroupId);
  }, []);

  const clientGroupColumns: KstDataGridColumn[] = useMemo(() => [
    { field: 'id', name: 'ID', size: 'hidden' },
    {
      field: 'name',
      name: 'Client Name',
      size: 'col-xs-3',
      sortable: true,
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.name)) {
          const disabledClass = e.isEnabled === false ? 'kst-client-disabled' : '';
          return `<a href="#" onclick="(window.__clientGroupMgmt && window.__clientGroupMgmt.viewClientGroup(${e.id})); return false;" class="${disabledClass}">${e.name}</a>`;
        }
        return 'Error getting client name.';
      },
    },
    {
      field: 'numSites',
      name: '# of Sites',
      size: 'col-xs-1',
      sortable: true,
      template: (e: any) => {
        if (hasValue(e)) {
          const disabledClass = e.isEnabled === false ? 'kst-client-disabled' : '';
          return `<span class="${disabledClass}">${e.numSites || 0}</span>`;
        }
        return '0';
      },
    },
    {
      field: 'locAddress',
      name: 'Address',
      size: 'col-xs-2',
      sortable: true,
      template: (e: any) => {
        if (hasValue(e)) {
          const disabledClass = e.isEnabled === false ? 'kst-client-disabled' : '';
          return `<span class="${disabledClass}">${e.locAddress || ''}</span>`;
        }
        return '';
      },
    },
    {
      field: 'locCity',
      name: 'City',
      size: 'col-xs-2',
      sortable: true,
      template: (e: any) => {
        if (hasValue(e)) {
          const disabledClass = e.isEnabled === false ? 'kst-client-disabled' : '';
          return `<span class="${disabledClass}">${e.locCity || ''}</span>`;
        }
        return '';
      },
    },
    {
      field: 'locState',
      name: 'State',
      size: 'col-xs-1',
      sortable: true,
      template: (e: any) => {
        if (hasValue(e)) {
          const disabledClass = e.isEnabled === false ? 'kst-client-disabled' : '';
          return `<span class="${disabledClass}">${e.locState || ''}</span>`;
        }
        return '';
      },
    },
    {
      field: 'viewEdit',
      name: 'View/Edit',
      size: 'col-xs-1',
      template: (e: any) => {
        if (hasValue(e) && hasValue(e.id)) {
          return `<a href="#" onclick="(window.__clientGroupMgmt && window.__clientGroupMgmt.viewClientGroup(${e.id})); return false;">View / Edit</a>`;
        }
        return '';
      },
    },
    {
      field: 'isEnabled',
      name: 'Status',
      size: 'col-xs-1',
      template: (e: any) => {
        if (!hasValue(e)) return '';
        if (e.isEnabled) {
          return `<button type="button" class="kstc-btn kstc-btn--success kstc-btn--sm" onclick="(window.__clientGroupMgmt && window.__clientGroupMgmt.toggleEnabled(${e.id}, true))">Enabled</button>`;
        } else {
          return `<button type="button" class="kstc-btn kstc-btn--danger kstc-btn--sm" onclick="(window.__clientGroupMgmt && window.__clientGroupMgmt.toggleEnabled(${e.id}, false))">Disabled</button>`;
        }
      },
    },
  ], []);

  const clientGroupMobileColumns: KstDataGridColumn[] = useMemo(() => [
    {
      field: 'locAddress',
      name: 'Location',
      size: 'col-xs-5',
      template: (e: any) => {
        if (hasValue(e)) {
          const disabledClass = e.isEnabled === false ? 'kst-client-disabled' : '';
          const address = e.locAddress || '';
          const city = e.locCity || '';
          const state = e.locState || '';
          const location = [address, city, state].filter(Boolean).join(', ');
          return `<span class="${disabledClass}">${location}</span>`;
        }
        return '';
      },
    },
    {
      field: 'isEnabled',
      name: 'Status',
      size: 'col-xs-3',
      template: (e: any) => {
        if (!hasValue(e)) return '';
        if (e.isEnabled) {
          return `<button type="button" class="kstc-btn kstc-btn--success kstc-btn--sm" onclick="(window.__clientGroupMgmt && window.__clientGroupMgmt.toggleEnabled(${e.id}, true))">Enabled</button>`;
        } else {
          return `<button type="button" class="kstc-btn kstc-btn--danger kstc-btn--sm" onclick="(window.__clientGroupMgmt && window.__clientGroupMgmt.toggleEnabled(${e.id}, false))">Disabled</button>`;
        }
      },
    },
  ], []);

  const rowActions: KstDataGridRowAction[] = useMemo(() => [
    { name: 'View', icon: 'fa-eye', event: 'view', class: 'btn-primary', mobileOnly: true },
  ], []);

  const rows: KstDataGridRows = useMemo(() => ({ entries }), [entries]);
  const pager: KstDataGridPager = useMemo(
    () => ({ curPage: null as any, numPages: null as any, records: null as any, isVisible: false }),
    []
  );

  const refreshClientGroupList = useCallback((showDisabled?: boolean) => {
    setIsClientGroupsLoading(true);
    const shouldShowDisabled = showDisabled !== undefined ? showDisabled : showingDisabledClients;
    const requestData = {
      data: {
        take: PAGE_SIZE,
        skip: 0,
        page: 1,
        pageSize: PAGE_SIZE,
        filter: {
          logic: 'and',
          filters: shouldShowDisabled ? [] : [{ field: 'isEnabled', operator: 'eq', value: true }],
        },
      },
    };

    api.getCGMng(1, PAGE_SIZE.toString(), JSON.stringify(requestData), (result: any) => {
      const newEntries = result.entries || [];
      for (const entry of newEntries) {
        entry.isChecked = false;
      }
      setEntries(newEntries);
      setIsClientGroupsLoading(false);
    }, () => {
      App.showPopupNotification('Error loading client groups.', 'error');
      setIsClientGroupsLoading(false);
    });
  }, [showingDisabledClients]);

  // Expose functions on window for onclick handlers in templates
  useEffect(() => {
    (window as any).__clientGroupMgmt = {
      viewClientGroup,
      toggleEnabled: (id: number, currentIsEnabled: boolean) => {
        api.updateCGMng(id, !currentIsEnabled, () => {
          refreshClientGroupList();
        });
      },
    };
    return () => {
      delete (window as any).__clientGroupMgmt;
    };
  }, [viewClientGroup, refreshClientGroupList]);

  // Load on mount
  useEffect(() => {
    refreshClientGroupList(false);
  }, []);

  const handleButtonCommand = useCallback((functionName: string, _checkedItems: any[]) => {
    if (functionName === 'add') {
      navigateToAdminClientGroup();
    } else if (functionName === 'toggledisabled') {
      setShowingDisabledClients((prev) => {
        const newValue = !prev;
        setIsClientGroupsLoading(true);
        // Refresh with new value directly since state update is async
        const requestData = {
          data: {
            take: PAGE_SIZE,
            skip: 0,
            page: 1,
            pageSize: PAGE_SIZE,
            filter: {
              logic: 'and',
              filters: newValue ? [] : [{ field: 'isEnabled', operator: 'eq', value: true }],
            },
          },
        };
        api.getCGMng(1, PAGE_SIZE.toString(), JSON.stringify(requestData), (result: any) => {
          const newEntries = result.entries || [];
          for (const entry of newEntries) {
            entry.isChecked = false;
          }
          setEntries(newEntries);
          setIsClientGroupsLoading(false);
        }, () => {
          App.showPopupNotification('Error loading client groups.', 'error');
          setIsClientGroupsLoading(false);
        });
        return newValue;
      });
    }
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortField((prevField) => {
      if (prevField === field) {
        // Same column: toggle direction.
        setSortDirection((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }
      // New column: default to ascending.
      setSortDirection('asc');
      return field;
    });
  }, []);

  const handleRowAction = useCallback((event: { action: string; row: any }) => {
    if (event.action === 'view' && event.row.id) {
      viewClientGroup(event.row.id);
    }
  }, [viewClientGroup]);

  return (
    <div className={theme}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <h3 className="kst-page-title">Client Management</h3>
                <p className="kst-page-description">
                  Manage clients for your organization. Click on a client name or View/Edit to manage the client and their sites.
                </p>
              </div>

              <KstDataGrid
                columns={clientGroupColumns}
                rows={rows}
                headerbuttons={headerButtons}
                pager={pager}
                mobilecolumns={clientGroupMobileColumns}
                mobiletitlefield="name"
                showcheckbox={false}
                rowactions={rowActions}
                loading={isClientGroupsLoading}
                sortfield={sortField}
                sortdirection={sortDirection}
                onSort={handleSort}
                onButtonCommand={handleButtonCommand}
                onRowAction={handleRowAction}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientGroupManagementScreen;
